// ---- Pure hunting sim (plan 11) ---------------------------------------------
// The Stalking shot rule + the Trapping soak stepper, extracted the same way
// src/sim/fishing.ts was: the game server ticks/judges these, and the studio
// Skill-Sim hunting driver imports the LITERAL same rules. Same purity rule as
// combat.ts/fishing.ts — no index.ts / src/server/* imports, no document/
// window/Bun.*/process.env. rng stays OUTSIDE (the shot roll, the haul rolls),
// band selection stays with the caller (items/define.ts `bandFor` is THE one
// copy of that rule), and loot tables are arguments (prey docs / the trap's
// own `haul`, rolled by src/loot.ts rollLoot) — so a parity test stays
// deterministic. The prey AI itself is NOT here: flee/roam/respawn come free
// from src/sim/combat.ts stepNpc (prey are ordinary wildlife NPCs).
//
// This module is also the ONE home for the hunting-economy DEFAULTS (the
// meaning of each number + the values the deterministic tests pin). The LIVE
// values are content: content/tunables/hunting.json overrides these
// field-by-field via src/tunables.ts (HUNTING_TUNING) — the server handlers,
// the client HUD, and the studio Skill-Sim all read THAT, never these
// constants directly (and never re-declare one locally). The studio's
// Apply → JSON writes the doc, which is how tuned pacing ships to the game.

// ---- Tuning DEFAULTS shared by both sides (plan 11 CP1 locked numbers) --------
export const TRAP_SOAK = 480;        // seconds a trap soaks before it's ready (× box soakMul)
export const TRAP_CAPACITY_MIN = 2;  // small game per haul (uniform roll, rng outside)
export const TRAP_CAPACITY_MAX = 4;
export const TRAP_COUNT = 1;         // deployed traps allowed at once (+ box trapCount)
export const TRAP_RANGE = 6;         // max reach to deploy / collect a trap (xz units)

/** The aimed shot's hit ceiling — no bonus stack makes a stalk a guarantee. */
export const AIM_HIT_CAP = 0.95;
/** Trophy odds multiplier per trophy tier (Keen Eye ≈ triples the base odds). */
export const TROPHY_TIER_MUL = 3;

/** Stalking XP per clean take, by prey KIND (plan 11 curve; ~7-12 takes to 100). */
export const STALKING_XP: Record<string, number> = { rabbit: 8, fox: 15, deer: 30 };
export const STALKING_XP_DEFAULT = 8;
/** Trapping XP per small game landed in a haul (a 2-4 haul → 8-16). */
export const TRAPPING_XP_PER_GAME = 4;
/** Huntsman standing deeds: both branches feed the same patron. */
export const HUNTSMAN_PER_TAKE = 10;
export const HUNTSMAN_PER_HAUL = 15;
export const HUNTSMAN_PER_TROPHY = 30;

// ---- The Stalking shot -------------------------------------------------------
// A drawn shot resolves as ONE roll against the weapon's distance band plus the
// stalker's folded aim bonus (bow marker + boxes), capped. The caller picks the
// band (items/define.ts bandFor — the shared band rule) and supplies the roll,
// so the server's fire handler and the arena's tracer judge identically.

/** Effective hit chance for a stalked shot: the rolled band's hit% plus the
 *  folded aim bonus, clamped to [0, AIM_HIT_CAP]. THE one copy of the fold —
 *  the server's fire handler and the studio driver both price a shot here. */
export function stalkHitChance(bandHit: number, aimHitBonus: number): number {
  return Math.min(AIM_HIT_CAP, Math.max(0, bandHit + aimHitBonus));
}

/** Judge a stalked shot: `roll` ∈ [0,1) supplied by the caller (rng outside). */
export function resolveStalkShot(bandHit: number, aimHitBonus: number, roll: number): boolean {
  return roll < stalkHitChance(bandHit, aimHitBonus);
}

/** Trophy odds at a trophy tier: the authored base chance scaled by
 *  TROPHY_TIER_MUL per tier (tier 0 = base; Keen Eye's tier 1 ≈ ×3), capped at
 *  certainty. ONE copy — the server's prey-loot roll and the studio's take
 *  roll both scale through it (`mul` defaults to the sim constant; pass the
 *  tunables doc's value for live pacing). */
export function trophyChance(base: number, tier: number, mul: number = TROPHY_TIER_MUL): number {
  return Math.min(1, base * Math.pow(mul, Math.max(0, tier)));
}

// ---- Trapping soak stepper -----------------------------------------------------
// The server keys deployed traps on ABSOLUTE epoch `readyAt` (they persist
// across downtime, like fishing nets / NPC respawns) — this dt-stepper is the
// studio/test half of the same rule, for arenas with no wall clock.

export type TrapState = {
  /** Seconds of soak left (0 = ready to collect). */
  soakLeft: number;
};

export type TrapEvent = { kind: "trapReady" };

const TRAP_READY: TrapEvent[] = [{ kind: "trapReady" }];

export function startTrapSoak(soakTime: number): TrapState {
  return { soakLeft: soakTime };
}

/** Count a trap's soak down; emits `trapReady` exactly once, on the crossing. */
export function stepTrap(s: TrapState, dt: number): TrapEvent[] {
  if (s.soakLeft <= 0) return [];
  s.soakLeft -= dt;
  return s.soakLeft <= 0 ? TRAP_READY : [];
}
